import {z} from 'zod';
import {organizationIdSchema} from './id';

/**
 * The day an organization's users are all deleted, as a calendar day in
 * `Europe/Amsterdam`. A client that stops using the tool on a fixed date asks for this;
 * `docs/adr/0018-soft-deletion-by-anonymisation.md` says what "deleted" means here.
 *
 * Null is the whole of "off": a date and a separate switch could disagree, and there is
 * nothing a disabled-but-remembered date buys that re-typing it does not.
 */
export const scheduledUserDeletionSchema = z.iso.date();

/**
 * The longest domain an address can carry: 253 characters, the DNS limit on a fully
 * qualified name (RFC 1035 §2.3.4, and RFC 5321 §4.5.3.1.2 for the address it sits in).
 */
export const EMAIL_DOMAIN_MAX_LENGTH = 253;

/**
 * Dot-separated labels, at least two of them, each at most 63 characters (RFC 1035 §2.3.4).
 *
 * Both bounds keep out a domain no mailbox can sit in, and an organization that saved one
 * would have locked its invitations to an address that cannot exist: a single label is a
 * host on somebody's local network, and a 64-character label resolves nowhere at all.
 */
const EMAIL_DOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * The domain an organization restricts its invitations to, written bare: `elaad.nl`, no
 * `@`. The separator belongs to the address, not to the setting, and storing it would put
 * it in every comparison as well.
 *
 * Normalised and then validated through a `.pipe()`, the way `emailSchema` is. The pipe is
 * what keeps the two halves apart in the generated document: a pattern lifted off the half
 * that runs *after* normalisation would publish a rule the API does not apply, and a
 * validator reading it would refuse a padded or upper-case domain the API accepts.
 */
export const emailDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().max(EMAIL_DOMAIN_MAX_LENGTH).regex(EMAIL_DOMAIN_PATTERN));

/** Everything after the last `@` — the one separator an address is guaranteed to have. */
export const emailDomainOf = (email: string): string => email.slice(email.lastIndexOf('@') + 1).toLowerCase();

/**
 * Compared case-insensitively and on the whole domain: `ada@mail.stedin.nl` is not in
 * `stedin.nl`. A suffix match would let anyone who owns `not-stedin.nl` in.
 */
export const isEmailInDomain = (email: string, domain: string): boolean =>
  emailDomainOf(email) === domain.trim().toLowerCase();

export const ORGANIZATION_STATUSES = ['ACTIVE', 'SUSPENDED'] as const;

export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export const organizationStatusSchema = z.enum(ORGANIZATION_STATUSES);

export const organizationSchema = z.object({
  id: organizationIdSchema,
  name: z.string().trim().min(1).max(200),
  status: organizationStatusSchema,
  /**
   * Only addresses in this domain may be invited. Null is off; the rule is enforced when
   * a user is created, and nowhere near sign-in — an account that exists was already let
   * through here.
   */
  emailDomain: z.string().max(EMAIL_DOMAIN_MAX_LENGTH).nullable(),
  scheduledUserDeletionOn: scheduledUserDeletionSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Organization = z.infer<typeof organizationSchema>;

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** Omitted and explicit `null` both mean the same thing: invite anyone. */
  emailDomain: emailDomainSchema.nullable().optional(),
});

export type CreateOrganization = z.infer<typeof createOrganizationSchema>;

export const updateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    status: organizationStatusSchema,
    /** Explicit `null` lifts the restriction; omitting the field leaves it as it was. */
    emailDomain: emailDomainSchema.nullable(),
    /** Explicit `null` turns the schedule off; omitting the field leaves it as it was. */
    scheduledUserDeletionOn: scheduledUserDeletionSchema.nullable(),
  })
  .partial()
  .refine(value => Object.keys(value).length > 0, {message: 'NO_FIELDS_TO_UPDATE'});

export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;
